import {estimateRunningThresholdPace} from '../utils/estimateRunningThresholdPace';
import {chartManager, type RunningProfile} from '../services/api';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { athleteSettingsApi } from '../services/athleteSettingsApi';
import { readinessThresholds } from '../utils/readinessThresholds';
import { displayDigits } from '../utils/displayDigits';
import { formatRunningPace } from '../utils/runningWorkoutMetrics';
import { AthleteSettingsModal, type AthleteSettingsView } from './athlete-settings/AthleteSettingsModal';
import './ReadinessThresholds.css';

export function ReadinessThresholds({ athleteId }: { athleteId: number }) {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<{ id: number; zones: Record<string, unknown> } | null>(null);
  const [running, setRunning] = useState<{id: number; profile: RunningProfile} | null>(null);
  const [view, setView] = useState<AthleteSettingsView | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setSnapshot(null);
    athleteSettingsApi.getAthleteZones(athleteId).then(res => {
      if (!cancelled) setSnapshot({ id: athleteId, zones: res.data.data.zones ?? {} });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [athleteId, revision]);
  useEffect(() => {
    let active = true;
    setRunning(null);
    chartManager.getRunningProfile(athleteId, {period: '90d'}).then(response => {
      if (active) setRunning({id: athleteId, profile: response.data.data});
    }).catch(() => {});
    return () => {active = false;};
  }, [athleteId, revision]);
  const zones = snapshot?.id === athleteId ? snapshot.zones : {};
  const { ftp, lthr, pace } = readinessThresholds(zones, running?.id === athleteId ? running.profile : null);
  const estimate = estimateRunningThresholdPace(running?.id === athleteId ? running.profile : null);
  const fields: Array<{ label: string; value: string; unit: string; view: AthleteSettingsView }> = [];
  if (ftp) fields.push({ label: t('athleteSettings.powerEditor.ftpLabel'), value: String(Math.round(ftp)), unit: t('athleteSettings.powerEditor.watts'), view: 'zones-power' });
  if (lthr) fields.push({ label: t('athleteSettings.trainingEditor.lthr'), value: String(Math.round(lthr)), unit: t('athleteSettings.trainingEditor.bpm'), view: 'zones-hr' });
  if (pace) fields.push({ label: t(estimate ? 'runningMetrics.thresholdEstimated' : 'runningMetrics.threshold'), value: formatRunningPace(pace), unit: t('runningMetrics.paceUnit'), view: 'zones-speed' });
  return <>
    {fields.length > 0 && <div className="readiness-thresholds">
      {fields.map(field => <button key={field.view} type="button" onClick={() => setView(field.view)} title={field.view === 'zones-speed' && estimate ? t('runningMetrics.thresholdEstimateSource', {date: estimate.date.slice(0, 10), workoutId: estimate.activityId}) : undefined}>
        <span>{field.label}</span><strong><bdi>{displayDigits(field.value)}</bdi> <small>{field.unit}</small></strong>
        {field.view === 'zones-speed' && estimate && <small>{t('runningMetrics.thresholdEstimateBasis')}</small>}
      </button>)}
    </div>}
    {view && <AthleteSettingsModal athleteId={athleteId} initialView={view} onClose={() => setView(null)} onSaved={() => setRevision(n => n + 1)} />}
  </>;
}
